import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Button, Dropdown, Input, Table } from 'antd';
import type { MenuProps, TableColumnsType } from 'antd';
import {
  DeleteOutlined,
  EditOutlined,
  EnvironmentOutlined,
  IdcardOutlined,
  MailOutlined,
  MoreOutlined,
  PhoneOutlined,
  PlusOutlined,
  SearchOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import FactoryFormModal from '../components/factories/FactoryFormModal';
import type { FactoryDto, FactoryInput } from '../dto/factory';
import {
  createFactoryRecord,
  deleteFactoryRecord,
  fetchFactories,
  updateFactoryRecord,
} from '../modules/factories/api/factoryApi';
import {
  canDeleteFactories,
  canManageFactories,
} from '../modules/factories/factoryAccessPolicy';
import { getFactoryErrorCode, getFactoryErrorMessage } from '../modules/factories/factoryErrorMessages';
import { authService } from '../services/auth';
import { isMarketing } from '../modules/marketing/marketingAccessPolicy';
import { useFeedback } from '../utils/feedback';
import '../components/layout/AppPage.css';
import './Factories.css';

const SEARCH_FIELDS: Array<keyof FactoryDto> = ['name', 'taxId', 'address', 'contactPerson', 'email', 'phone'];

export function matchesFactorySearch(factory: FactoryDto, searchText: string): boolean {
  const needle = searchText.trim().toLocaleLowerCase('pl');
  if (!needle) return true;
  return SEARCH_FIELDS.some((field) => {
    const value = factory[field];
    return typeof value === 'string' && value.toLocaleLowerCase('pl').includes(needle);
  });
}

type ModalState = { open: false } | { open: true; factory: FactoryDto | null };

const Factories: React.FC = () => {
  const { t } = useTranslation(['factories', 'common']);
  const { modal } = App.useApp();
  const feedback = useFeedback();
  const [factories, setFactories] = useState<FactoryDto[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [modalState, setModalState] = useState<ModalState>({ open: false });
  const [submitting, setSubmitting] = useState(false);
  const [nameError, setNameError] = useState<string | null>(null);

  const currentUser = authService.getCurrentUser();
  const canEdit = canManageFactories(currentUser);
  const canDelete = canDeleteFactories(currentUser);
  const emptyValue = t('factories:emptyValue');

  const loadFactories = useCallback(async () => {
    setLoading(true);
    try {
      setFactories(await fetchFactories());
    } catch {
      feedback.error(t('factories:messages.fetchFailed'));
    } finally {
      setLoading(false);
    }
  }, [feedback, t]);

  useEffect(() => {
    void loadFactories();
    // Load once on mount; later refreshes are explicit after each mutation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleFactories = useMemo(
    () => factories.filter((factory) => matchesFactorySearch(factory, searchText)),
    [factories, searchText],
  );

  const openCreate = () => {
    setNameError(null);
    setModalState({ open: true, factory: null });
  };

  const openEdit = (factory: FactoryDto) => {
    setNameError(null);
    setModalState({ open: true, factory });
  };

  const closeModal = () => {
    setNameError(null);
    setModalState({ open: false });
  };

  const handleSubmit = async (payload: FactoryInput) => {
    if (!modalState.open) return;
    const editing = modalState.factory;
    setSubmitting(true);
    setNameError(null);
    try {
      if (editing) {
        const saved = await updateFactoryRecord(editing.id, payload);
        feedback.success(t('factories:messages.updateSuccess', { name: saved.name }));
      } else {
        const saved = await createFactoryRecord(payload);
        feedback.success(t('factories:messages.createSuccess', { name: saved.name }));
      }
      setModalState({ open: false });
      await loadFactories();
    } catch (error) {
      const code = getFactoryErrorCode(error);
      if (code === 'FACTORY_NAME_DUPLICATE') {
        setNameError(t('factories:messages.duplicateName'));
      } else if (code === 'FACTORY_NOT_FOUND') {
        feedback.error(t('factories:messages.notFound'));
        setModalState({ open: false });
        await loadFactories();
      } else {
        feedback.error(getFactoryErrorMessage(error, t, 'factories:messages.saveFailed', { name: payload.name }));
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (factory: FactoryDto) => {
    const confirmed = await new Promise<boolean>((resolve) => {
      modal.confirm({
        title: t('factories:confirmDelete.title', { name: factory.name }),
        content: t('factories:confirmDelete.content'),
        okText: t('factories:confirmDelete.ok'),
        cancelText: t('factories:confirmDelete.cancel'),
        okButtonProps: { danger: true },
        onOk: () => resolve(true),
        onCancel: () => resolve(false),
      });
    });
    if (!confirmed) return;

    try {
      await deleteFactoryRecord(factory.id);
      feedback.success(t('factories:messages.deleteSuccess', { name: factory.name }));
    } catch (error) {
      const code = getFactoryErrorCode(error);
      feedback.error(
        getFactoryErrorMessage(error, t, 'factories:messages.deleteFailed', { name: factory.name }),
        code === 'FACTORY_IN_USE' ? { sticky: true } : undefined,
      );
    }
    await loadFactories();
  };

  const getRowMenuItems = (factory: FactoryDto): MenuProps['items'] => {
    const items: NonNullable<MenuProps['items']> = [];
    if (canEdit) {
      items.push({ key: 'edit', icon: <EditOutlined />, label: t('factories:rowMenu.edit') });
    }
    if (canDelete) {
      const inUse = (factory.supplierOrderCount ?? 0) > 0;
      items.push({
        key: 'delete',
        icon: <DeleteOutlined />,
        label: inUse ? t('factories:rowMenu.deleteBlocked') : t('factories:rowMenu.delete'),
        danger: !inUse,
        disabled: inUse,
      });
    }
    return items;
  };

  const renderContact = (factory: FactoryDto) => {
    const lines = [
      factory.contactPerson ? { icon: <UserOutlined />, text: factory.contactPerson } : null,
      factory.phone ? { icon: <PhoneOutlined />, text: factory.phone } : null,
      factory.email ? { icon: <MailOutlined />, text: factory.email } : null,
    ].filter((line): line is { icon: React.ReactElement; text: string } => line !== null);

    if (lines.length === 0) return <span className="factory-muted">{emptyValue}</span>;
    return (
      <div className="factory-contact">
        {lines.map((line) => (
          <span key={line.text} className="factory-contact__line">
            {line.icon}
            <span>{line.text}</span>
          </span>
        ))}
      </div>
    );
  };

  const columns: TableColumnsType<FactoryDto> = [
    {
      title: t('factories:table.factory'),
      key: 'name',
      width: 280,
      render: (_value, factory) => (
        <div className="factory-identity">
          {canEdit ? (
            <button
              type="button"
              className="factory-identity__name-button"
              onClick={() => openEdit(factory)}
            >
              <span className="factory-identity__name">{factory.name}</span>
            </button>
          ) : (
            <span className="factory-identity__name">{factory.name}</span>
          )}
          <span className="factory-identity__meta">
            <IdcardOutlined /> {factory.taxId || t('factories:noTaxId')}
          </span>
        </div>
      ),
    },
    {
      title: t('factories:table.contact'),
      key: 'contact',
      width: 230,
      render: (_value, factory) => renderContact(factory),
    },
    {
      title: t('factories:table.address'),
      key: 'address',
      width: 250,
      render: (_value, factory) => (
        <span className="factory-address">
          <EnvironmentOutlined />
          <span title={factory.address || undefined}>{factory.address || emptyValue}</span>
        </span>
      ),
    },
    {
      title: t('factories:table.supplierOrders'),
      key: 'supplierOrderCount',
      // „Zamówienia do dostawcy” on one line at 1440 px.
      width: 190,
      align: 'right',
      render: (_value, factory) => (
        factory.supplierOrderCount
          ? <span className="factory-count">{factory.supplierOrderCount}</span>
          : <span className="factory-muted">{t('factories:supplierOrdersNone')}</span>
      ),
    },
  ];

  if (isMarketing(currentUser)) {
    const orderCountIndex = columns.findIndex((column) => column.key === 'supplierOrderCount');
    if (orderCountIndex >= 0) columns.splice(orderCountIndex, 1);
  }

  if (canEdit || canDelete) {
    columns.unshift({
      title: t('factories:table.actions'),
      key: 'menu',
      width: 56,
      align: 'center',
      fixed: 'left',
      render: (_value, factory) => (
        <Dropdown
          trigger={['click']}
          menu={{
            items: getRowMenuItems(factory),
            onClick: ({ key, domEvent }) => {
              domEvent.stopPropagation();
              if (key === 'edit') openEdit(factory);
              if (key === 'delete') void handleDelete(factory);
            },
          }}
        >
          <Button
            type="text"
            icon={<MoreOutlined />}
            onClick={(event) => event.stopPropagation()}
            aria-label={t('factories:rowMenu.open')}
          />
        </Dropdown>
      ),
    });
  }

  return (
    <div className="app-page factories-page">
      <section className="app-page__card">
        <div className="app-page__toolbar">
          <Input
            className="app-page__toolbar-search"
            placeholder={t('factories:searchPlaceholder')}
            prefix={<SearchOutlined />}
            value={searchText}
            onChange={(event) => setSearchText(event.target.value)}
            allowClear
          />
          <div className="app-page__toolbar-actions">
            <span className="app-page__count" aria-label={t('factories:countLabel')}>
              {visibleFactories.length}
            </span>
            {canEdit && (
              <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
                {t('factories:addFactory')}
              </Button>
            )}
          </div>
        </div>

        <Table
          className="factories-table"
          columns={columns}
          dataSource={visibleFactories}
          rowKey="id"
          loading={loading}
          scroll={{ x: 900 }}
          locale={{
            emptyText: searchText.trim() ? t('factories:noFactoriesFound') : t('factories:noFactories'),
          }}
        />
      </section>

      <FactoryFormModal
        open={modalState.open}
        factory={modalState.open ? modalState.factory : null}
        submitting={submitting}
        nameError={nameError}
        onSubmit={(payload) => void handleSubmit(payload)}
        onCancel={closeModal}
      />
    </div>
  );
};

export default Factories;
